// PORTED COPY — A20 W2b. Original: TPRM7 packages/ui/src/reporter/types.ts
// (@vendorwatch/ui). The licence server is a separate repo with a separate
// lockfile and a Vite alias of '@' → './src' only, so it cannot import
// @vendorwatch/ui; it shares code the way it already shares the design-system
// tokens — by ported source. Keep this file byte-comparable with the original
// so `diff` stays the review tool. Changes belong upstream first.
//
// A20 · Polestead Reporter — shared types.
//
// The envelope is the ONE contract between every capture surface and the single
// ingest endpoint. Five apps across two repos post this exact shape; the license
// server parses it strictly at the envelope level and leniently per-field.
//
// Keep `SCHEMA_VERSION` in lockstep with the server's accepted set
// (backend/src/routes/bug-report.routes.ts). The server rejects unknown
// versions rather than guessing — same posture as trust-telemetry.

export const SCHEMA_VERSION = 1;

/** Which product surface a report came from. Drives GitHub repo routing. */
export type SurfaceKey =
  // TPRM7 (issues → Polestead/TPRM7)
  | 'tprm7.admin'
  | 'tprm7.portal'
  | 'tprm7.trust'
  // License server (issues → Polestead/TPRM--License)
  | 'license.console'
  | 'license.portal';

export type ReportSeverity = 'blocker' | 'major' | 'minor' | 'question';

/**
 * Capture mode.
 *
 * `report` — everyone. Masked, short buffer, becomes a support ticket.
 * `studio` — super-admins only, and the SERVER re-checks that (a UI gate is not
 *            a security boundary). Longer buffer, optionally unmasked, becomes a
 *            reusable help asset instead of a ticket.
 */
export type ReporterMode = 'report' | 'studio';

/**
 * Who and where. Every field is optional because the anonymous auth-page
 * reporter has almost none of it — a locked-out user still needs to file.
 *
 * NOTE (A20 C8): `organizationId` is NOT currently exposed client-side in
 * apps/admin (useCurrentUser drops it) and NO app exposes a version string.
 * Both are plumbed as part of W2; until then these arrive undefined and the
 * report is still actionable via `route` + `surfaceKey`.
 */
export interface ReportIdentity {
  surfaceKey: SurfaceKey;
  /** Build version, e.g. "2026.8.3". Undefined until each app defines one. */
  appVersion?: string;
  organizationId?: string;
  userId?: string;
  /** Present only on on-prem installs (apps/portal has it today). */
  instanceId?: string;
  licenseId?: string;
  /** Role/persona label, for reproducing under the right permissions. */
  role?: string;
  /** Current route. The single most useful field when everything else is absent. */
  route: string;
}

/** Non-identifying client facts that make a bug reproducible. */
export interface ReportClient {
  userAgent: string;
  viewport: { width: number; height: number };
  /** Resolved reporter locale (BCP-47), e.g. "pt-BR". */
  locale: string;
  /** IANA zone — timing bugs are otherwise unreproducible. */
  timeZone?: string;
  /** Honoured by the widget itself, and worth knowing for UI bugs. */
  prefersReducedMotion?: boolean;
  colorScheme?: 'light' | 'dark';
}

/** One captured console line. Mirrors the rrweb console plugin's payload. */
export interface ConsoleEntry {
  level: string;
  payload: string[];
  /** ms since capture start. */
  at: number;
  trace?: string[];
}

/**
 * One captured request. Deliberately NOT bodies — a TPRM response body is
 * customer data. URLs have their query string stripped unless it is empty,
 * because query params carry tokens (`?t=` ring tokens in apps/trust).
 */
export interface NetworkEntry {
  method: string;
  /** Origin + pathname only. Query and hash are removed at capture time. */
  url: string;
  status?: number;
  durationMs?: number;
  /** ms since capture start. */
  at: number;
  /** Set when the request threw rather than returning a status. */
  failed?: boolean;
}

/**
 * What the user actually said, plus the consent they gave.
 *
 * `includeVisibleText` is a consent record, not a rendering hint: when false the
 * events were captured masked and the text is not recoverable from them. It is
 * stored so a reviewer can tell "no text because masked" from "no text because
 * the page was empty".
 */
export interface ReportBody {
  title: string;
  description: string;
  severity: ReportSeverity;
  includeVisibleText: boolean;
  /** Set in studio mode — names the help asset instead of a ticket. */
  assetName?: string;
}

/**
 * The recording payload. `events` is the rrweb event array, gzip'd and base64'd
 * by the transport before it leaves the browser (A20 C9 — a zipped JSON blob
 * passes `_shared/file-validation.ts`'s `zip_ooxml` family unchanged, where a
 * .webm would be rejected as `unknown`).
 */
export interface ReportCapture {
  /** rrweb events. Empty array when capture was unavailable or declined. */
  events: unknown[];
  console: ConsoleEntry[];
  network: NetworkEntry[];
  /** Wall-clock span the buffer covers, ms. */
  durationMs: number;
  /** False when the reporter fell back to no-capture (rrweb failed to load). */
  captured: boolean;
  /** Mirrors ReportBody.includeVisibleText at the moment record() was started. */
  masked: boolean;
}

export interface ReportEnvelope {
  schemaVersion: number;
  generatedAt: string;
  mode: ReporterMode;
  identity: ReportIdentity;
  client: ReportClient;
  report: ReportBody;
  capture: ReportCapture;
}

/** Result of a submit attempt, surfaced honestly in the dialog. */
export type SubmitResult =
  | { ok: true; ticketRef?: string; queued?: false }
  | { ok: true; queued: true }
  | { ok: false; error: string };

export interface ReporterConfig {
  /** Absolute ingest URL, or a same-origin path. */
  endpoint: string;
  surfaceKey: SurfaceKey;
  appVersion?: string;
  /**
   * Resolves identity at submit time, not mount time — role and org can change
   * mid-session (persona switch), and the auth-page reporter has none of it.
   */
  getIdentity?: () => Omit<ReportIdentity, 'surfaceKey' | 'appVersion' | 'route'>;
  /**
   * Whether studio mode is offered. The server re-checks this independently;
   * passing true here only shows the affordance.
   */
  studioAvailable?: boolean;
  /** Override the auto-detected locale. */
  locale?: string;
  /** Disable capture entirely (privacy mode / a customer who opts out). */
  captureDisabled?: boolean;
  /**
   * Rolling buffer window in ms. rrweb takes a fresh full snapshot every
   * window, and we retain the last two — so a report carries between one and
   * two windows of context. Default 30s ⇒ 30–60s retained.
   */
  bufferMs?: number;
  /** Replaces the built-in fetch transport (used by the on-prem queue, W5). */
  transport?: (envelope: ReportEnvelope) => Promise<SubmitResult>;
}

/**
 * Elements carrying this class are never recorded at all — not masked, omitted.
 * Use it on anything that would be a breach even in redacted form: the license
 * key reveal panel, backup codes, the recovery-link modal.
 */
export const NO_CAPTURE_CLASS = 'ps-no-capture';

/**
 * Elements carrying this class have their text masked EVEN IN REVEAL MODE.
 *
 * `ps-no-capture` and `ps-mask` answer different questions and must not be
 * confused: blocked content is omitted from the recording entirely (a breach
 * even redacted — keys, backup codes, recovery links), whereas masked content
 * is still recorded as a shaped placeholder so the replay keeps its layout and
 * a reviewer can see WHERE the data was without seeing WHAT it was.
 *
 * rrweb resolves this with `el.closest()` (rrweb-snapshot `needMaskingText`,
 * checkAncestors = true), so putting the class on a container masks every text
 * node beneath it — annotate the smallest wrapper that holds the field, not
 * each leaf.
 */
export const MASK_CLASS = 'ps-mask';

/**
 * Attribute form of MASK_CLASS.
 *
 * Exists because half of these products style with Tailwind utility strings and
 * the other half with `style={{}}`; a `className` on a component that forwards
 * `className` into a variant helper can be silently dropped or overwritten,
 * while `data-ps-mask` survives every one of those paths. Either marker works;
 * both are matched by REVEAL_MASK_SELECTOR.
 */
export const MASK_ATTR = 'data-ps-mask';

/**
 * The selector rrweb masks with WHEN THE REPORTER HAS OPTED INTO REVEALING TEXT.
 *
 * THE DEFECT THIS CLOSES. Reveal mode used to drop `maskTextSelector: '*'` and
 * fall back to `maskTextClass` alone — and that class had ZERO appliers in
 * either repo, so "include visible text" revealed every text node on the page
 * rather than "everything except what we promised to keep back". The escape
 * hatch was documented, shipped, and protected nothing.
 *
 * Hand-annotation alone cannot fix that, because it fails silently: a new
 * customer-contact panel that nobody remembered to mark just leaks, and no test
 * anywhere goes red. So this selector pairs the annotations with STRUCTURAL
 * rules that hold on surfaces nobody has touched yet:
 *
 *   .ps-mask / [data-ps-mask]  the explicit opt-in, for a panel a human judged
 *                              sensitive.
 *   a[href^="mailto:"]         an address is PII wherever it appears, and this
 *   a[href^="tel:"]            is how every contact row in both products
 *                              renders one. Structural, so it covers surfaces
 *                              written after this line.
 *   input/textarea/select      belt and braces. `maskAllInputs` already covers
 *   [contenteditable]          the VALUE; this covers text nodes that live
 *                              inside such an element (rich-text editors,
 *                              option labels).
 *
 * WHAT THIS IS NOT. It is not a PII detector and must never be described as
 * one. rrweb does not mask attributes, so `alt`, `title`, `aria-label` and URLs
 * still carry whatever the product put in them, and a reveal-mode capture is
 * customer data whatever this selector matches. It narrows the blast radius of
 * a consented reveal; it does not make one safe.
 */
export const REVEAL_MASK_SELECTOR = [
  `.${MASK_CLASS}`,
  `[${MASK_ATTR}]`,
  'a[href^="mailto:"]',
  'a[href^="tel:"]',
  'input',
  'textarea',
  'select',
  '[contenteditable]',
].join(', ');

